import type { MarkerBreakdownData } from "@/components/chat/marker-breakdown"
import type { PricingCardWire } from "@/components/chat/pricing-breakdown"

/** One parsed SSE event. Field set mirrors what glue/lp-contextual emits
 *  (see chat-panel.tsx). */
export interface SseEvent {
  event: string
  text?: string
  reason?: string
  matched?: string
  chatwoot_url?: string
  locale?: string
  category?: string
  language?: string
  conversation_id?: number
  pubsub_token?: string
  auth_token?: string
  email_collect?: boolean
  /** Milliseconds to wait after a handoff before offering an optional email
   *  (`session` event). 0 disables the prompt. Served by glue so the threshold
   *  is tunable without a deploy here. */
  slow_reply_prompt_ms?: number
  /** Whether a human was reachable when the handoff fired (`handoff` event).
   *  glue's authoritative verdict — prefer it over re-deriving locally. */
  online?: boolean
  /** Inverse of `online` on the `handoff` event. */
  out_of_hours?: boolean
  message_id?: string
  /** Chatwoot message id of a bot answer glue just posted to Chatwoot
   *  (`chatwoot_echo` event). The widget suppresses this id when it echoes
   *  back over the Chatwoot subscription so the answer isn't rendered twice. */
  chatwoot_message_id?: number
  /** Progress events: which stage the bot is in while it works. */
  phase?: "understanding" | "retrieving" | "searching"
  /** 1-based sub-topic index for the `searching` phase (multi-topic scan). */
  index?: number | null
  /** Total sub-topics for the `searching` phase. */
  total?: number | null
  /** Knowledge-base pages the bot cited for this turn (`sources` event).
   *  Sanitized server-side: host-allowlisted, deduped, capped 0–4. */
  sources?: { label: string; url?: string }[]
  /** Prior transcript replayed when glue continues an existing conversation for
   *  a fresh tab (`history` event, AHD-10752 server-side stitch). The panel had
   *  no local turns for this conversation — its sessionStorage was empty — so
   *  glue sends the earlier turns to render above the new one. `agent` = a human
   *  agent's reply. */
  turns?: { role: "user" | "bot" | "agent"; content: string }[]
  /** On the `session` event: true when glue CONTINUED an existing conversation
   *  (AHD-10752 stitch) rather than creating a new one. The widget uses it to
   *  suppress the buffered launcher-greeting echo — a greeting must not land
   *  mid-thread in the stitched Chatwoot conversation. */
  stitched?: boolean
  /** Files/images on this turn (AHD-7851). Dormant until the backend emits it;
   *  shape mirrors ChatAttachment so it maps straight onto the turn. */
  attachments?: {
    id: string
    kind: "image" | "file"
    url: string
    name?: string
    mime?: string
    sizeBytes?: number
  }[]
  /** Deterministic biomarker-list breakdown (`marker_breakdown` event) — the
   *  resolver's projected facts, rendered as the MarkerBreakdown card. */
  markerBreakdown?: MarkerBreakdownData
  /** Deterministic package/price card (`pricing_card` event, AHD-11297) — glue
   *  emits the authoritative package prices; the widget builds the full card. */
  pricingCard?: PricingCardWire
  /** Follow-up suggestion chips (`suggested_questions` event): 0–3 tappable next
   *  questions rendered beneath the answer. Text is model- or template-authored
   *  and rendered as text (never innerHTML). `source` = LLM-generated vs the
   *  static medical-safe set. */
  questions?: { text: string; key: string }[]
  source?: "generated" | "static"
  /** When `source === "static"`, which gate routed this turn to the static pair
   *  (glue's `StaticReason`) — measurement only, optional so this stays
   *  backward-compatible with glue builds that don't emit it. */
  static_reason?: string
}

export interface SseParseResult {
  events: SseEvent[]
  /** Unconsumed trailing partial frame — feed back in with the next chunk. */
  rest: string
  /** True once a `[DONE]` sentinel was seen. */
  done: boolean
}

/** Pull all complete `data: …\n\n` frames from `buffer`. Pure: no I/O, no state. */
export function parseSseFrames(buffer: string): SseParseResult {
  const events: SseEvent[] = []
  let done = false
  let frameEnd: number
  while ((frameEnd = buffer.indexOf("\n\n")) !== -1) {
    const frame = buffer.slice(0, frameEnd)
    buffer = buffer.slice(frameEnd + 2)
    const line = frame.split("\n").find((l) => l.startsWith("data:"))
    if (!line) continue
    const payload = line.slice(5).trim()
    if (payload === "[DONE]") {
      done = true
      break
    }
    try {
      events.push(JSON.parse(payload) as SseEvent)
    } catch {
      continue
    }
  }
  return { events, rest: buffer, done }
}
